import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Sparkles, BookOpen, Tag, SkipForward } from 'lucide-react';
import { SYSTEM_SHELVES, type ShelfAnalysis, type ShelfMapping } from '@/lib/shelfMappingUtils';
import { cn } from '@/lib/utils';
import { useState } from 'react';

interface ShelfMappingRowProps {
  shelf: ShelfAnalysis;
  mapping: ShelfMapping;
  onMappingChange: (mapping: ShelfMapping) => void;
}

export function ShelfMappingRow({ shelf, mapping, onMappingChange }: ShelfMappingRowProps) {
  const [customName, setCustomName] = useState(
    mapping.targetType === 'custom_shelf' ? mapping.targetValue : shelf.name
  );

  const handleTypeChange = (value: string) => {
    // Parse the value to determine type and target
    if (value === 'skip') {
      onMappingChange({ ...mapping, targetType: 'skip', targetValue: '' });
    } else if (value === 'tag') {
      onMappingChange({ ...mapping, targetType: 'tag', targetValue: shelf.name });
    } else if (value === 'custom') {
      onMappingChange({ ...mapping, targetType: 'custom_shelf', targetValue: customName });
    } else {
      // It's a system shelf value
      onMappingChange({ ...mapping, targetType: 'system_shelf', targetValue: value });
    }
  };

  const getCurrentValue = (): string => {
    if (mapping.targetType === 'skip') return 'skip';
    if (mapping.targetType === 'tag') return 'tag';
    if (mapping.targetType === 'custom_shelf') return 'custom';
    return mapping.targetValue; // system shelf value
  };

  const isSuggested = (value: string): boolean => {
    const suggestion = shelf.suggestedMapping;
    if (suggestion.type === 'system_shelf' && suggestion.value === value) return true;
    if (suggestion.type === 'tag' && value === 'tag') return true;
    if (suggestion.type === 'custom_shelf' && value === 'custom') return true;
    return false;
  };

  return (
    <div className="border rounded-lg p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium">"{shelf.name}"</span>
          <Badge variant="secondary" className="text-xs">
            {shelf.bookCount} {shelf.bookCount === 1 ? 'book' : 'books'}
          </Badge>
          {shelf.isExclusive && (
            <Badge variant="outline" className="text-xs">
              Exclusive
            </Badge>
          )}
        </div>
        {shelf.suggestedMapping.confidence === 'high' && (
          <Badge className="bg-primary/10 text-primary border-primary/20 gap-1">
            <Sparkles className="w-3 h-3" />
            Suggested
          </Badge>
        )}
      </div>

      {/* Mapping Options */}
      <RadioGroup
        value={getCurrentValue()}
        onValueChange={handleTypeChange}
        className="space-y-2"
      >
        {/* System Shelves */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {SYSTEM_SHELVES.map((sysShelf) => (
            <div key={sysShelf.value} className="flex items-center space-x-2">
              <RadioGroupItem 
                value={sysShelf.value} 
                id={`${shelf.name}-${sysShelf.value}`}
                className="min-w-4 min-h-4"
              />
              <Label 
                htmlFor={`${shelf.name}-${sysShelf.value}`}
                className={cn(
                  "text-sm cursor-pointer flex items-center gap-1",
                  isSuggested(sysShelf.value) && "font-medium text-primary"
                )}
              >
                <span>{sysShelf.icon}</span>
                <span className="truncate">{sysShelf.label}</span>
              </Label>
            </div>
          ))}
        </div>

        <div className="border-t pt-2 mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
          {/* Create Custom Shelf */}
          <div className="flex items-center space-x-2">
            <RadioGroupItem 
              value="custom" 
              id={`${shelf.name}-custom`}
              className="min-w-4 min-h-4"
            />
            <Label 
              htmlFor={`${shelf.name}-custom`}
              className={cn(
                "text-sm cursor-pointer flex items-center gap-1",
                isSuggested('custom') && "font-medium text-primary"
              )}
            >
              <BookOpen className="w-3 h-3" />
              Custom Shelf
            </Label>
          </div>

          {/* Add as Tag */}
          <div className="flex items-center space-x-2">
            <RadioGroupItem 
              value="tag" 
              id={`${shelf.name}-tag`}
              className="min-w-4 min-h-4"
            />
            <Label 
              htmlFor={`${shelf.name}-tag`}
              className={cn(
                "text-sm cursor-pointer flex items-center gap-1",
                isSuggested('tag') && "font-medium text-primary"
              )}
            >
              <Tag className="w-3 h-3" />
              Add as Tag
            </Label>
          </div>

          {/* Skip */}
          <div className="flex items-center space-x-2">
            <RadioGroupItem 
              value="skip" 
              id={`${shelf.name}-skip`}
              className="min-w-4 min-h-4"
            />
            <Label 
              htmlFor={`${shelf.name}-skip`}
              className="text-sm cursor-pointer flex items-center gap-1 text-muted-foreground"
            >
              <SkipForward className="w-3 h-3" />
              Skip
            </Label>
          </div>
        </div>

        {/* Custom name input when custom shelf selected */}
        {mapping.targetType === 'custom_shelf' && (
          <div className="pt-2">
            <Label htmlFor={`${shelf.name}-custom-name`} className="text-xs text-muted-foreground">
              Custom shelf name:
            </Label>
            <Input
              id={`${shelf.name}-custom-name`}
              value={customName}
              onChange={(e) => {
                setCustomName(e.target.value);
                onMappingChange({ ...mapping, targetValue: e.target.value });
              }}
              placeholder="Enter shelf name..."
              className="mt-1 h-8"
            />
          </div>
        )}
      </RadioGroup>
    </div>
  );
}
